// @ts-strict-ignore
import { ChangeDetectionStrategy, Component, HostBinding, Input, OnDestroy, OnInit, ViewChildren, QueryList } from '@angular/core';
import { LMFieldBaseComponent } from '@field/lm-field-base.component';
import { LMModel } from '@form/model';
import { LMFieldServiceAdapter } from '@field/lm-field-service-adapter';
import { BehaviorSubject } from "rxjs";
import * as urltemplate from "url-template";
import { LMFormRequestService } from "@form/services";
import { SohoCheckBoxComponent } from 'ids-enterprise-ng';

enum LMCheckboxGroupDisplayEnum {
  PLAIN = 0,
  DISPLAY_AS_CHECKBOXES
}

@Component({
  selector:    'lm-checkboxgroup',
  templateUrl: './lm-checkboxgroup.component.html',
  styleUrls:   [ 'lm-checkboxgroup.component.scss' ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LMCheckboxGroupComponent extends LMFieldBaseComponent implements OnInit, OnDestroy {
  @Input() layoutObject: LMCheckboxButtonLayout;
  @Input() specFields: LMSpecFields;
  @Input() model: LMModel;
  @Input() lmColumnLayoutInfo: LMColumnLayoutInfo;
  @Input() lmParagraphLayoutInfo: LMParagraphLayoutInfo;

  @ViewChildren(SohoCheckBoxComponent) checkboxes: QueryList<SohoCheckBoxComponent>;

  @HostBinding('attr.data-automation-id') dataFieldAutomationId = '';

  public _model: Array<LMStateValue> = [];
  public isHorizontal = false;
  private isUserDefined = false;
  public values: Array<LMStateValue> = [];
  public values$ = new BehaviorSubject<LMStateValue[]>([]);

  public displayType: LMCheckboxGroupDisplayEnum;
  public layoutDisplayType;

  constructor(
    protected lmFieldServiceAdapter: LMFieldServiceAdapter,
    private lmFormRequestService: LMFormRequestService,
  ) {
    super(lmFieldServiceAdapter);
    this.layoutDisplayType = LMCheckboxGroupDisplayEnum;
  }

  ngOnInit() {
    super.ngOnInit();
    this.initialize(this.layoutObject, this.specFields, this.model);

    this.subscriptionArray.push(this.modelField.clearState.subscribe(() => {
      this.values.forEach((value:LMStateValue) => {
        value.selected = false;
      })

      this._model = [];
      this.modelField.toDataValue(this._model, this.transformOptions);
      // clear value
    }));

    if (this.layoutObject.displayAsCheckboxes || this.layoutObject.horizontalCheckboxes) {
      this.subscribeToInlineError();
      this.displayType = LMCheckboxGroupDisplayEnum.DISPLAY_AS_CHECKBOXES;
    } else {
      this.subscribeToInlineError();
      this.displayType = LMCheckboxGroupDisplayEnum.PLAIN;
    }

    this.isUserDefined = this.specField.hasUserDefinedStates;
    this.isHorizontal = this.field.horizontalCheckboxes;

    if (this.isUserDefined) {
      this.getUserDefinedStates();
    } else if (this.specField.stateValues) {
      if (this.layoutObject.limitedStates?.length > 0) {
        this.values = this.layoutObject.limitedStates;
        this.addValueToLimitedStates();
      } else {
        this.values = this.specField.stateValues;
      }

      this.values.forEach((state:LMStateValue) => {
        state.selected = false;
        if (this.specField?.configuration?.init?.value === state.value) {
          state.selected = true;
          this._model.push(state.value);
          this.modelField.toDataValue(this._model, this.transformOptions);
        }
      })
      this.values$.next(this.values);
    }

    this.setFieldClasses();

    this.dataFieldAutomationId = `lm-${this.fieldAutomationId}-field`;
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }

  public selectItem(item?: LMStateValue) {
    if (item.selected) {
      item.selected = false;

      const index = this._model.indexOf(item.value);
      if (index > -1) {
        this._model.splice(index, 1);
      }
    } else {
      item.selected = true;
      this._model.push(item.value);
    }

    this.modelField.toDataValue(this._model, this.transformOptions);
  }

  trackByValue(index: number, state: LMStateValue) {
    return state.value;
  }

  public getUserDefinedStates(): void {
    this.busyIndicator = true;

    const compiledFormRequestUrl = urltemplate.parse(this.model.formUrlInfo.formRequestUrl);
    const formRequestUrl = compiledFormRequestUrl.expand({ resourceId: this.model.formResourceId });

    const dataView = this.model.getData();
    const actionName = this.model.action;

    this.lmFormRequestService
      .getUserDefinedStates(formRequestUrl, this.bindId, dataView, actionName)
      .subscribe((result: LMUserDefinedResponse) => {
        if (this.lmFieldServiceAdapter.lmContextService.userDefinedStatesPageSizeAndIndicator()) {
          if (result && result.hasOwnProperty('states') && Array.isArray(result.states)) {
            this.values = this.modelField.states = this.lmFieldServiceAdapter.lmFormatUtilService.fieldStatesToOptionsArray(result.states, false);
            this.values$.next(this.values);
            return;
          }
        } else {
          if (Array.isArray(result)) {
            this.values = this.modelField.states = this.lmFieldServiceAdapter.lmFormatUtilService.fieldStatesToOptionsArray(result, false);
            this.values$.next(this.values);
            return;
          }
        }

        // an error
        this.busyIndicator = false;
        this.lmFieldServiceAdapter.lmLoggerService.error([ 'Error in getUserDefinedStates', this.bindId, result ]);
      },
      (error) => {
        this.busyIndicator = false;
        this.lmFieldServiceAdapter.lmLoggerService.error([ 'Error in getUserDefinedStates', this.bindId, error ]);
      });
  };

  private addValueToLimitedStates(): void {
    if (!this.lmFieldServiceAdapter.lmFieldUtilService.isBlank(this.specField.type, this.modelField.value)) {
      // value set on the field, make sure it is in the limited states array
      if (!this.values.some((state: LMStateValue) => state.value === this.modelField.value) ) {
        const state: LMStateValue = this.modelField.states.filter((state: LMStateValue) => state.value === this.modelField.value)[0];
        this.values.unshift(state);
      }
    }
  }
}
